import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';

const STORAGE_KEY = 'assistant.open';

export type AssistantStatus = 'idle' | 'asking' | 'waitingForHuman' | 'unreadAnswer';

interface AssistantContextType {
    isOpen: boolean;
    status: AssistantStatus;
    toggle: () => void;
    open: () => void;
    close: () => void;
    setStatus: (status: AssistantStatus) => void;
}

const AssistantContext = createContext<AssistantContextType | null>(null);

// Returns the assistant context, or null when no AssistantProvider is mounted.
export const useAssistant = () => useContext(AssistantContext);

interface ProviderProps {
    children: React.ReactNode;
    // When true, panel state is restored from localStorage on mount and saved on change.
    persist?: boolean;
}

export const AssistantProvider: React.FC<ProviderProps> = ({children, persist = true}) => {
    const [isOpen, setIsOpen] = useState<boolean>(() => {
        if (!persist) return false;
        try {
            return localStorage.getItem(STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const [status, setStatus] = useState<AssistantStatus>('idle');

    useEffect(() => {
        if (!persist) return;
        try {
            localStorage.setItem(STORAGE_KEY, isOpen ? 'true' : 'false');
        } catch {
            // ignore quota / privacy mode errors
        }
    }, [isOpen, persist]);

    const toggle = useCallback(() => setIsOpen(v => !v), []);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    return (
        <AssistantContext.Provider value={{isOpen, status, toggle, open, close, setStatus}}>
            {children}
        </AssistantContext.Provider>
    );
};
